import React, { useMemo } from 'react';
import EventLabel from '../../components/EventLabel/EventLabel';
import ConfirmationHotelRow from './ConfirmationHotelRow';
import { getCalculatedOrderRow } from './utils';

function TableHeader({ displayConfirmationSettings }) {
  return (
    <>
      <tr className="cms-table-row whitespace-nowrap">
        {displayConfirmationSettings['ShowHotelName'] && (
          <th align="right">
            <EventLabel name="OrderRowHotelName" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowRoomType'] && (
          <th align="right">
            <EventLabel name="OrderRowRoomType" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowSize'] && (
          <th align="right">
            <EventLabel name="OrderRowRoomSize" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowOccupants'] && (
          <th align="right">
            <EventLabel name="OrderRowOccupants" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowNights'] && (
          <th align="right">
            <EventLabel name="OrderRowNights" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowCheckin'] && (
          <th align="right">
            <EventLabel name="OrderRowCheckin" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowCheckout'] && (
          <th align="right">
            <EventLabel name="OrderRowCheckout" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowPriceExVat'] && (
          <th align="right">
            <EventLabel name="OrderRowPriceExVat" groupName="CMS.OrderSummary" />
          </th>
        )}

        {/* {displayConfirmationSettings['ShowConfDiscount'] ? (
          <th align="right">
            <EventLabel name="DiscountAmount" groupName="CMS.OrderSummary" />
          </th>
        ) : null} */}

        {displayConfirmationSettings['ShowVatAmount'] && (
          <th align="right">
            <EventLabel name="OrderRowVatSum" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowVatPercent'] && (
          <th align="right">
            <EventLabel name="OrderRowVatPercent" groupName="CMS.OrderSummary" />
          </th>
        )}
        {displayConfirmationSettings['ShowPriceTotal'] && (
          <th align="right">
            <EventLabel name="OrderRowRowTotal" groupName="CMS.OrderSummary" />
          </th>
        )}
      </tr>
    </>
  );
}

export default function HotelDetails({ hotels, hotelAllotments, displayConfirmationSettings, orders, ...props }) {
  const hotelAllotmentRows = useMemo(() => {
    if (hotels?.length === 0 || hotelAllotments?.length === 0) return [];

    const order = orders?.find(o => o.leader) ?? orders?.[0];
    const orderRows = order?.orderRow?.filter(or => or.hotelGroupId) ?? [];

    const result = orderRows
      ?.filter(o => o.hotelGroupId)
      ?.reduce((acc, orderRow) => {
        const hotelAllotment = hotelAllotments?.find(ha => ha.hotelAllotmentPricing?.some(hap => hap.allotmentPricingId === orderRow.hotelAllotmentPricingId));

        const roomMates = orderRow?.rooms?.length > 0 ? orderRow.rooms : orderRows?.find(o => o.rooms?.length > 0)?.rooms;

        if (!hotelAllotment) return acc;

        const hotel = hotels.find(h => h.hotelRoomType.some(y => y.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId));
        const roomType = hotel.hotelRoomType.find(hr => hr.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId);

        const pricingIds = [...orderRows].filter(or => or.hotelGroupId === orderRow.hotelGroupId).map(or => or.hotelAllotmentPricingId);

        const dateValues = hotelAllotments
          .filter(ha => ha.hotelAllotmentPricing?.some(hap => pricingIds.includes(hap.allotmentPricingId)))
          .map(ha => +new Date(ha.date.substring(0, 10)));

        const allotmentStartDate = Math.min(...dateValues);
        const allotmentEndDate = Math.max(...dateValues);

        const checkinDate = new Date(allotmentStartDate).toISOString().substring(0, 10);
        const checkoutDate = new Date(new Date(allotmentEndDate).setDate(new Date(allotmentEndDate).getDate() + 1)).toISOString().substring(0, 10);

        const roomSize = hotelAllotment?.hotelAllotmentPricing?.find(hap => hap.allotmentPricingId === orderRow.hotelAllotmentPricingId)?.roomSize;

        const nights = Math.ceil((new Date(checkoutDate) - new Date(checkinDate)) / (1000 * 60 * 60 * 24));

        if (!hotelAllotment) return acc;

        if (!acc.some(a => a.hotelGroupId === orderRow.hotelGroupId)) {
          const rowTotal = orderRows?.filter(or => or.hotelGroupId === orderRow.hotelGroupId)?.reduce((acc, or) => acc + or.amount, 0 ?? 0);
          const discountAmount =
            orderRows?.filter(or => or.hotelGroupId === orderRow.hotelGroupId)?.reduce((acc, or) => acc + (or.discountAmount ?? 0), 0 ?? 0) ?? 0;
          const vatAmount = (rowTotal - discountAmount) * (orderRow.vatPercent / 100);

          return [
            ...acc,
            {
              ...orderRow,
              nights,
              roomSize,
              amount: rowTotal,
              vatAmount: vatAmount,
              rooms: roomMates,
              rowTotal: rowTotal + vatAmount,
              hotelName: `${hotel.name}`,
              roomType: `${roomType.name}`,
              startDateTime: checkinDate,
              endDateTime: checkoutDate,
            },
          ];
        }
        return acc;
      }, []);

    return result;
  }, [hotelAllotments, orders, hotels]);

  return (
    <div className="cms-table-container mt-4">
      <table className="cms-table small overflow-x-auto" aria-label="spanning table">
        <thead>
          <TableHeader displayConfirmationSettings={displayConfirmationSettings} />
        </thead>
        <tbody>
          {hotelAllotmentRows?.map(row => (
            <ConfirmationHotelRow {...props} key={row.orderRowId} displayConfirmationSettings={displayConfirmationSettings} orderRow={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
